import { ajvResolver } from '@hookform/resolvers/ajv'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { useCustomTheme } from 'contexts/theme'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import RequestPermissionSchema, { IRequestPermissionFormData } from 'schema/authentication/request-permission'
import api from 'services/api'

interface PermissionObj {
  keyPermission:
    | 'dados-cadastrais-titulares'
    | 'dados-cadastrais-dependentes'
    | 'dados-informacoes-empresa'
    | 'dados-contratos-trabalhos-titulares'
    | 'dados-historico-cadastrais'
    | 'dados-historico-afastamentos'
    | 'dados-historico-contratos-trabalho'
    | 'dados-informacoes-folha'
  isPermitted: boolean
}

interface SellerOrganization {
  corporateName: string
}

const useComponent = () => {
  const [sellerOrganization, setSellerOrganization] = useState<SellerOrganization | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const { updateUserData, user } = useAuth()
  const { closeAlert, openAlert } = useAlert()
  const { theme } = useCustomTheme()
  const history = useHistory()

  const form = useForm<IRequestPermissionFormData>({
    resolver: ajvResolver(RequestPermissionSchema),
    defaultValues: {
      cadastraisDosTitulastes: true,
      cadastraisDosDependentes: true,
      informacoesDaEmpresa: true,
      contratosDeTrabalhosDosTitulares: true,
      historicoDeDadosCadastrais: true,
      historicoDeAfastamentos: true,
      historicoDeContratosDeTrabalho: true,
      informacoesDaFolha: true,
    },
  })

  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    form.setValue('cadastraisDosTitulastes', checked)
    form.setValue('cadastraisDosDependentes', checked)
    form.setValue('informacoesDaEmpresa', checked)
    form.setValue('contratosDeTrabalhosDosTitulares', checked)
    form.setValue('historicoDeDadosCadastrais', checked)
    form.setValue('historicoDeAfastamentos', checked)
    form.setValue('historicoDeContratosDeTrabalho', checked)
    form.setValue('informacoesDaFolha', checked)
  }

  const onSubmit: SubmitHandler<IRequestPermissionFormData> = useCallback(
    async (data) => {
      try {
        await validateIsAllowToMakeRequestPermission()

        const permissionList: PermissionObj[] = [
          {
            keyPermission: 'dados-cadastrais-titulares',
            isPermitted: data.cadastraisDosTitulastes,
          },
          {
            keyPermission: 'dados-cadastrais-dependentes',
            isPermitted: data.cadastraisDosDependentes,
          },
          {
            keyPermission: 'dados-informacoes-empresa',
            isPermitted: data.informacoesDaEmpresa,
          },
          {
            keyPermission: 'dados-contratos-trabalhos-titulares',
            isPermitted: data.contratosDeTrabalhosDosTitulares,
          },
          {
            keyPermission: 'dados-historico-cadastrais',
            isPermitted: data.historicoDeDadosCadastrais,
          },
          {
            keyPermission: 'dados-historico-afastamentos',
            isPermitted: data.historicoDeAfastamentos,
          },
          {
            keyPermission: 'dados-historico-contratos-trabalho',
            isPermitted: data.historicoDeContratosDeTrabalho,
          },
          {
            keyPermission: 'dados-informacoes-folha',
            isPermitted: data.informacoesDaFolha,
          },
        ]

        await api.post('/organizations-permissions/multiple-create', {
          permissionList: permissionList,
        })

        await updateUserData()

        return history.push('/redirect')
      } catch (error) {
        openAlert('Ops! Algum erro aconteceu!', 'error')
      }
    },
    [history, openAlert, updateUserData]
  )

  const onSubmitTables = async (data: IRequestPermissionFormData) => {
    try {
      await validateIsAllowToMakeRequestPermission()

      const permissionList: PermissionObj[] = [
        {
          keyPermission: 'dados-cadastrais-titulares',
          isPermitted: data.cadastraisDosTitulastes,
        },
        {
          keyPermission: 'dados-cadastrais-dependentes',
          isPermitted: data.cadastraisDosDependentes,
        },
        {
          keyPermission: 'dados-informacoes-empresa',
          isPermitted: data.informacoesDaEmpresa,
        },
        {
          keyPermission: 'dados-contratos-trabalhos-titulares',
          isPermitted: data.contratosDeTrabalhosDosTitulares,
        },
        {
          keyPermission: 'dados-historico-cadastrais',
          isPermitted: data.historicoDeDadosCadastrais,
        },
        {
          keyPermission: 'dados-historico-afastamentos',
          isPermitted: data.historicoDeAfastamentos,
        },
        {
          keyPermission: 'dados-historico-contratos-trabalho',
          isPermitted: data.historicoDeContratosDeTrabalho,
        },
        {
          keyPermission: 'dados-informacoes-folha',
          isPermitted: data.informacoesDaFolha,
        },
      ]

      await api.post('/organizations-permissions/multiple-create', {
        permissionList: permissionList,
      })

      // await updateUserData()

      return history.push('/esocial-connect')
    } catch (error) {
      openAlert('Ops! Algum erro aconteceu!', 'error')
    }
  }

  const validateIsAllowToMakeRequestPermission = async () => {
    const validateRequestPermissions = await api.get('/organizations-permissions/user/validate')

    if (validateRequestPermissions.data.data) {
      return history.push('/esocial-connect')
    }

    // Nota: isLoading agora é controlado pela função loadInitialData
    // não setamos mais aqui para evitar race condition
  }

  const onLoadOrganizationSellerName = async () => {
    await api
      .get('/invites/organization/invite-name')
      .then((response) => setSellerOrganization(response.data.data))
      .catch((error) => openAlert(error.response?.data.data.message ?? 'Ops! Algum erro aconteceu!', 'error'))
  }

  const handleChange = (formField: any) => closeAlert()

  const handleGoBack = () => history.push('/datasource-connect')

  // Função para carregar dados iniciais e aguardar ambas as chamadas
  const loadInitialData = async () => {
    try {
      // Aguarda AMBAS as chamadas completarem antes de liberar o loading
      // Isso previne o race condition onde validateIsAllowToMakeRequestPermission
      // poderia terminar antes de onLoadOrganizationSellerName
      await Promise.all([
        onLoadOrganizationSellerName(),
        validateIsAllowToMakeRequestPermission()
      ])
    } catch (error) {
      // Erros já são tratados individualmente em cada função
      console.error('Erro ao carregar dados iniciais:', error)
    } finally {
      // Garante que isLoading sempre será setado para false, mesmo em caso de erro
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadInitialData()
  }, [user])

  const indeterminateValues =
    !form.watch('cadastraisDosTitulastes') ||
    !form.watch('cadastraisDosDependentes') ||
    !form.watch('informacoesDaEmpresa') ||
    !form.watch('contratosDeTrabalhosDosTitulares') ||
    !form.watch('historicoDeDadosCadastrais') ||
    !form.watch('historicoDeAfastamentos') ||
    !form.watch('historicoDeContratosDeTrabalho') ||
    !form.watch('informacoesDaFolha')

  const checkedValues =
    form.watch('cadastraisDosTitulastes') &&
    form.watch('cadastraisDosDependentes') &&
    form.watch('informacoesDaEmpresa') &&
    form.watch('contratosDeTrabalhosDosTitulares') &&
    form.watch('historicoDeDadosCadastrais') &&
    form.watch('historicoDeAfastamentos') &&
    form.watch('historicoDeContratosDeTrabalho') &&
    form.watch('informacoesDaFolha')

  const optionsCount = Object.keys(form.watch()).length
  const trueOptionsCount = Object.values(form.watch()).filter((value) => value === true).length

  return {
    isLoading,
    form,
    theme,
    sellerOrganization,
    user,
    optionsCount,
    trueOptionsCount,
    checkedValues,
    indeterminateValues,
    onSubmit,
    handleSelectAll,
    handleChange,
    handleGoBack,
    onSubmitTables
  }
}

export default useComponent
